import React, { useState } from 'react';
import type { ReviewComment } from '../types';

interface CommentListProps {
  comments: ReviewComment[];
  onApplySelected: (selected: ReviewComment[]) => void;
  /** Comment IDs currently being applied (edit + reply + resolve in flight). */
  applyingIds?: Set<string>;
}

/**
 * Renders a list of review comments with selection checkboxes.
 * Users select one or more comments, then click "Apply selected"
 * to resolve them as targeted feedback.
 */
export function CommentList({ comments, onApplySelected, applyingIds }: CommentListProps) {
  const unresolved = comments.filter((c) => !c.resolved);
  const resolved = comments.filter((c) => c.resolved);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const isApplying = (applyingIds?.size ?? 0) > 0;

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleApply = () => {
    const items = unresolved.filter((c) => selected.has(c.id));
    if (items.length === 0) return;
    onApplySelected(items);
    setSelected(new Set());
  };

  if (comments.length === 0) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {unresolved.map((c) => (
        <CommentCard
          key={c.id}
          comment={c}
          checked={selected.has(c.id)}
          onToggle={() => toggle(c.id)}
          applying={applyingIds?.has(c.id)}
        />
      ))}

      {unresolved.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
          <button
            className="btn btn--accent btn--sm"
            disabled={selected.size === 0 || isApplying}
            onClick={handleApply}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            {isApplying && (
              <span className="spinner" style={{ width: 12, height: 12, borderWidth: 2 }} />
            )}
            {isApplying
              ? 'Applying…'
              : `Apply ${selected.size > 0 ? `${selected.size} selected` : 'selected'}`}
          </button>
          {unresolved.length > 1 && (
            <button
              className="btn btn--ghost btn--sm"
              disabled={isApplying}
              onClick={() => {
                if (selected.size === unresolved.length) {
                  setSelected(new Set());
                } else {
                  setSelected(new Set(unresolved.map((c) => c.id)));
                }
              }}
            >
              {selected.size === unresolved.length ? 'Deselect all' : 'Select all'}
            </button>
          )}
        </div>
      )}

      {resolved.length > 0 && (
        <details style={{ marginTop: 4 }}>
          <summary
            className="t-small muted"
            style={{ cursor: 'pointer', userSelect: 'none', marginBottom: 6 }}
          >
            {resolved.length} resolved comment{resolved.length !== 1 ? 's' : ''}
          </summary>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, opacity: 0.6 }}>
            {resolved.map((c) => (
              <CommentCard key={c.id} comment={c} muted />
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function CommentCard({
  comment,
  checked,
  onToggle,
  muted,
  applying,
}: {
  comment: ReviewComment;
  checked?: boolean;
  onToggle?: () => void;
  muted?: boolean;
  applying?: boolean;
}) {
  const interactive = !muted && !applying;
  return (
    <div
      style={{
        padding: '10px 12px',
        border: checked
          ? '1px solid var(--accent)'
          : '1px solid var(--line)',
        borderLeft: muted
          ? '1px solid var(--line)'
          : applying
          ? '3px solid var(--run)'
          : checked
          ? '3px solid var(--accent)'
          : '3px solid var(--line)',
        borderRadius: 4,
        background: checked
          ? 'color-mix(in srgb, var(--accent) 6%, var(--surface))'
          : 'var(--surface)',
        cursor: interactive ? 'pointer' : 'default',
        opacity: applying ? 0.85 : 1,
        transition: 'all 0.15s',
      }}
      onClick={interactive ? onToggle : undefined}
    >
      {/* header: checkbox + author + time + artifact */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 6,
          flexWrap: 'wrap',
        }}
      >
        {!muted && (
          <input
            type="checkbox"
            checked={checked ?? false}
            disabled={applying}
            onChange={onToggle}
            onClick={(e) => e.stopPropagation()}
            style={{ margin: 0, cursor: applying ? 'default' : 'pointer' }}
          />
        )}
        <span className="t-small" style={{ fontWeight: 600 }}>
          {comment.author}
        </span>
        <span className="t-micro muted">{relativeTime(comment.createdAt)}</span>
        {comment.documentTitle && (
          <>
            <span className="t-micro muted">·</span>
            <span
              className="t-micro"
              style={{
                background: 'var(--surface-2)',
                border: '1px solid var(--line)',
                borderRadius: 3,
                padding: '1px 6px',
              }}
            >
              {comment.documentTitle}
            </span>
          </>
        )}
        {applying && (
          <span
            className="t-micro"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              color: 'var(--run)',
              fontWeight: 600,
            }}
          >
            <span className="spinner" style={{ width: 11, height: 11, borderWidth: 2 }} />
            Applying…
          </span>
        )}
        {!applying && comment.resolved && (
          <span className="loz loz-green" style={{ height: 18, padding: '0 6px', fontSize: 10 }}>
            Resolved
          </span>
        )}
      </div>

      {/* highlighted text context */}
      {comment.highlightedText && (
        <div
          style={{
            marginLeft: muted ? 0 : 24,
            marginBottom: 4,
            padding: '4px 8px',
            borderLeft: '2px solid var(--accent)',
            background: 'color-mix(in srgb, var(--accent) 6%, var(--surface))',
            borderRadius: 2,
            fontSize: 12,
            color: 'var(--ink-2)',
            fontStyle: 'italic',
          }}
        >
          on: "{comment.highlightedText}"
        </div>
      )}

      {/* body */}
      <div
        className="t-small"
        style={{
          lineHeight: 1.5,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          marginLeft: muted ? 0 : 24,
        }}
      >
        {comment.body.length > 300 ? comment.body.slice(0, 297) + '...' : comment.body}
      </div>
    </div>
  );
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}
